<script lang="ts">
  /**
   * Quick-create-modal för härd-segment som öppnas från Rita-kartans
   * "klicka-på-runda + två-punkter"-flöde (segment-karta-flöde).
   *
   * Skiljer sig från SegmentsView-formen: minimalistisk, ingen WKT-
   * textarea (geometrin är pre-beräknad från slice-kalkylen i kartan),
   * ingen edit/delete. Submit skickar createSegment och stänger sig
   * själv.
   *
   * Hard-rules (CLAUDE.md, matchar SegmentsView):
   *  - Inga fritextfält — name är kategorisk identifierare max 100 tecken
   *  - Cookie-baserad auth + CSRF-header (görs i segmentsClient)
   *  - Inga råa databas-ID:n
   *  - Användartexter på svenska
   */
  import {
    createSegment,
    PRIORITIES,
    priorityLabel,
    type CreateSegmentInput,
    type SegmentPriority,
  } from '../hearth-segments/segmentsClient';

  type Props = {
    /** Runda som segmentet hör till. UUID från backend. */
    routeId: string;
    /** Beskrivande namn på rundan så admin ser kontext. */
    routeName: string;
    /**
     * Geometrin för segmentet som backend-kompatibel WKT
     * (`MULTILINESTRING((lng lat, lng lat, ...))`). Kallande
     * komponent har redan kört turf-slice och formaterat.
     */
    geometryWkt: string;
    /** Anropas vid lyckad create. Föräldern stänger modalen. */
    onCreated: () => void;
    /** Anropas vid avbryt eller backdrop-klick. */
    onCancel: () => void;
    /** Anropas vid 401/403 så App-shellen tvingar logout. */
    onUnauthorized?: () => void;
  };
  const { routeId, routeName, geometryWkt, onCreated, onCancel, onUnauthorized }: Props = $props();

  let formName: string = $state('');
  let formPriority: SegmentPriority = $state('medium');
  let formError: string | null = $state(null);
  let isSaving = $state(false);

  function validateForm(): string | null {
    if (formName.trim().length === 0) return 'Ange ett namn för segmentet.';
    if (formName.length > 100) return 'Namn max 100 tecken.';
    if (!geometryWkt.trim().toUpperCase().startsWith('MULTILINESTRING')) {
      return 'Geometrin är ogiltig — försök markera segmentet på nytt.';
    }
    return null;
  }

  async function handleSubmit(): Promise<void> {
    formError = null;
    const validation = validateForm();
    if (validation) {
      formError = validation;
      return;
    }
    isSaving = true;
    try {
      const input: CreateSegmentInput = {
        name: formName.trim(),
        geometryWkt: geometryWkt.trim(),
        priority: formPriority,
        activeFrom: null,
        activeTo: null,
      };
      const result = await createSegment(routeId, input);
      if (result.kind === 'ok') {
        onCreated();
      } else if (result.kind === 'unauthorized') {
        onUnauthorized?.();
      } else if (result.kind === 'invalid') {
        formError = result.message;
      } else if (result.kind === 'route_not_found') {
        formError = 'Rundan finns inte längre — välj en annan.';
      } else {
        formError =
          result.kind === 'forbidden'
            ? 'Saknar behörighet.'
            : result.kind === 'error'
              ? result.message
              : 'Något gick fel.';
      }
    } finally {
      isSaving = false;
    }
  }

  function handleBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget && !isSaving) {
      onCancel();
    }
  }

  function handleKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && !isSaving) {
      onCancel();
    }
  }
</script>

<svelte:window onkeydown={handleKeyDown} />

<div
  class="modal-backdrop"
  role="dialog"
  aria-modal="true"
  aria-labelledby="segment-quick-title"
  onclick={handleBackdropClick}
>
  <div class="modal" role="document">
    <div class="modal-header">
      <p class="eyebrow">Nytt härd-segment</p>
      <h3 id="segment-quick-title">Markera delsträcka på {routeName}</h3>
      <p class="dim">
        Sträckan beräknades från dina två klick längs rundan. Ge segmentet ett namn och prioritet —
        inga fritext-beskrivningar.
      </p>
    </div>

    <form
      class="form"
      onsubmit={(e) => {
        e.preventDefault();
        void handleSubmit();
      }}
    >
      <label class="field">
        <span>Namn (max 100 tecken)</span>
        <input type="text" maxlength="100" bind:value={formName} required disabled={isSaving} />
      </label>

      <label class="field">
        <span>Prioritet</span>
        <select bind:value={formPriority} disabled={isSaving}>
          {#each PRIORITIES as p (p)}
            <option value={p}>{priorityLabel(p)}</option>
          {/each}
        </select>
      </label>

      {#if formError}
        <div class="alert error" role="alert">{formError}</div>
      {/if}

      <div class="form-actions">
        <button type="submit" class="btn primary" disabled={isSaving}>
          {isSaving ? 'Sparar…' : 'Spara segment'}
        </button>
        <button type="button" class="btn secondary" onclick={onCancel} disabled={isSaving}>
          Avbryt
        </button>
      </div>
    </form>
  </div>
</div>

<style>
  .modal-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 1rem;
  }
  .modal {
    background: var(--bg-elev);
    border: 1px solid var(--rule);
    border-radius: 8px;
    padding: 1.5rem;
    max-width: 480px;
    width: 100%;
    max-height: 90vh;
    overflow-y: auto;
    color: var(--text);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.4);
  }
  .modal-header {
    display: grid;
    gap: 0.3rem;
    margin-bottom: 1rem;
  }
  .eyebrow {
    font-size: 0.8rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--accent);
    font-weight: 600;
    margin: 0;
  }
  h3 {
    margin: 0;
    color: var(--text);
    font-size: 1.05rem;
  }
  .dim {
    color: var(--text-dim);
    font-size: 0.85rem;
    margin: 0;
  }
  .form {
    display: grid;
    gap: 0.85rem;
  }
  .field {
    display: grid;
    gap: 0.25rem;
    font-size: 0.85rem;
    color: var(--text-dim);
  }
  .field input,
  .field select {
    background: var(--bg);
    border: 1px solid var(--rule);
    border-radius: 4px;
    color: var(--text);
    font-family: inherit;
    font-size: 0.95rem;
    padding: 0.45rem 0.6rem;
  }
  .field input:focus,
  .field select:focus {
    outline: 2px solid var(--accent);
    outline-offset: 1px;
  }
  .form-actions {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
    margin-top: 0.25rem;
  }
  .btn {
    background: transparent;
    border: 1px solid var(--rule);
    color: var(--text);
    padding: 0.5rem 1rem;
    border-radius: 4px;
    font-family: inherit;
    font-size: 0.9rem;
    cursor: pointer;
  }
  .btn.primary {
    background: var(--accent);
    border-color: var(--accent);
    color: var(--bg);
    font-weight: 600;
  }
  .btn.primary:hover:not(:disabled) {
    background: var(--glow);
    border-color: var(--glow);
  }
  .btn.secondary:hover:not(:disabled) {
    color: var(--glow);
    border-color: var(--text-dim);
  }
  .btn:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
  .alert {
    padding: 0.55rem 0.8rem;
    border-radius: 4px;
    font-size: 0.85rem;
  }
  .alert.error {
    background: var(--danger-soft);
    border: 1px solid var(--danger);
    color: var(--text);
  }
</style>
